import _ from 'lodash'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ContentHeader from './ContentHeader'
import PatientLocationDisplay from './PatientLocationDisplay'
import { IconButton, Menu, MenuItem } from '@mui/material'
import SpecialitiesAutoCompleteComponent, { SpecialityAutocompleteOption } from '@/components/ui/autocomplete/SpecialitiesAutoComplete'
import TextInput from '@/components/ui/inputs/TextInput'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'

type PatientLocationInfo = {
  description: string
  placeId?: string
}

export type ConsultationInfoProps = {
  reasons?: string[];
  selectedSpeciality: SpecialityAutocompleteOption | null;
  specialities: SpecialityAutocompleteOption[];
  onInputChange: (value: string) => void;
  onSpecialityChange: (event: any, newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null, typeOfEvent: string) => void;
  reason: string;
  onReasonChange: (newReason: string) => void;
  patientLocation?: PatientLocationInfo | null;
};

export function ConsultationInfoComponent(props: ConsultationInfoProps) {
  const { reasons = [], selectedSpeciality, specialities, onSpecialityChange, onReasonChange, onInputChange, reason, patientLocation } = props
  const { t } = useTranslation()

  const reasonInputContainer = useRef<HTMLDivElement>(null)


  const reasonOptions = useMemo(() => _.map(reasons, (reason) => ({ label: reason, value: reason })), [reasons])


  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    switch (field) {
      case 'reason':
        onReasonChange?.(value)
        break
      default:
        break
    }
  }
  // test
  const handleSpecialityChange = (event: any, newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null, typeOfEvent: string) => {
    onSpecialityChange(event, newValue, typeOfEvent)

  }

  const handleInputChange = (value: string) => {
    onInputChange(value)
  }


  return (
    <Container maxWidth="md" sx={{ pt: '20px', pb: '20px' }}>
      <Paper>
        <Stack sx={{ px: 3 }} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <ContentHeader sx={{ p: '20px 16px' }} title={t('page.liveConsultation.consultationInfo.title')} subtitle={t('page.liveConsultation.consultationInfo.description')} />
        </Stack>
        <Divider />
        <Stack spacing={2} sx={{ p: '20px 16px' }}>
          <SpecialitiesAutoCompleteComponent
            id="speciality"
            name={'speciality'}
            label={t('field.speciality.label')}
            editable={true}
            canDelete={true}
            multiple={false}
            selectedSpecialities={selectedSpeciality}
            specialities={specialities}
            onInputChange={handleInputChange}
            onSpecialityChange={handleSpecialityChange}
          />

          <Box ref={reasonInputContainer}>
            <TextInput
              fullWidth
              InputProps={{
                endAdornment: !reasonOptions.length ? null : (
                  <PopupState variant="popover" popupId="reason-options">
                    {(popupState) => (
                      <>
                        <IconButton size="small" {...bindTrigger(popupState)}>
                          <KeyboardArrowDownIcon />
                        </IconButton>
                        <Menu
                          {...bindMenu(popupState)}
                          disablePortal
                          anchorReference="anchorPosition"
                          anchorPosition={{
                            top: reasonInputContainer.current?.getBoundingClientRect().bottom || 0,
                            left: reasonInputContainer.current?.getBoundingClientRect().left || 0,
                          }}
                          PaperProps={{
                            sx: [
                              reasonInputContainer.current && {
                                minWidth: reasonInputContainer.current.getBoundingClientRect().width,
                              },
                            ],
                          }}
                        >
                          {_.map(reasonOptions, (option, index) => (
                            <MenuItem
                              key={`${option}-${index}`}
                              onClick={() => {
                                onReasonChange?.(option.value)
                                popupState.close()
                              }}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </PopupState>
                ),
              }}
              label={t('field.reason.label', { context: 'consultation' })}
              name="reason"
              id="reason"
              onChange={handleChange('reason')}
              value={reason}
            />
          </Box>

          {patientLocation?.description && (
            <PatientLocationDisplay description={patientLocation.description} />
          )}
        </Stack>
      </Paper>
    </Container >
  )
}

export default ConsultationInfoComponent
